import React from 'react';
import { useTranslation } from 'react-i18next';
import { PageSizeSelect } from '@/components/ui/PageSizeSelect';
import styles from './styles.module.scss';

export interface ListPaginationProps {
  /** 0-based page index. */
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Builds `data-testid`s: `${prefix}-page-size`, `-page-prev`, `-page-next`. */
  testIdPrefix: string;
  className?: string;
}

/**
 * List / table footer pager: page-size select, "11–19 of 19" range and
 * prev / next arrows. Renders nothing for an empty list.
 */
export const ListPagination: React.FC<ListPaginationProps> = ({
  page,
  pageSize,
  total,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  testIdPrefix,
  className,
}) => {
  const { t } = useTranslation('common');
  if (total <= 0) return null;

  const start = page * pageSize;
  const end = Math.min(start + pageSize, total);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className={[styles.footer, className].filter(Boolean).join(' ')}>
      <PageSizeSelect
        value={pageSize}
        options={pageSizeOptions}
        onChange={onPageSizeChange}
        testId={`${testIdPrefix}-page-size`}
      />
      <span className={styles.range}>
        {start + 1}–{end} {t('common:of')} {total}
      </span>
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 0}
        className={styles.arrow}
        aria-label={t('common:previousPage')}
        data-testid={`${testIdPrefix}-page-prev`}
      >
        ‹
      </button>
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages - 1}
        className={styles.arrow}
        aria-label={t('common:nextPage')}
        data-testid={`${testIdPrefix}-page-next`}
      >
        ›
      </button>
    </div>
  );
};

export default ListPagination;
